import React from "react";

import { cn } from "@/lib/utils";

type Phase = "typing" | "holding" | "deleting" | "pausing";

interface TypewriterProps {
  words: string[];
  typingSpeed?: number;
  deletingSpeed?: number;
  holdDuration?: number;
  pauseDuration?: number;
  className?: string;
  cursorClassName?: string;
}

export function Typewriter({
  words,
  typingSpeed = 80,
  deletingSpeed = 50,
  holdDuration = 2200,
  pauseDuration = 300,
  className,
  cursorClassName,
}: TypewriterProps) {
  const [wordIndex, setWordIndex] = React.useState(0);
  const [charIndex, setCharIndex] = React.useState(0);
  const [phase, setPhase] = React.useState<Phase>("typing");
  const [prefersReducedMotion, setPrefersReducedMotion] = React.useState(false);

  React.useEffect(() => {
    if (typeof window === "undefined") return;

    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(mediaQuery.matches);

    update();
    mediaQuery.addEventListener("change", update);
    return () => mediaQuery.removeEventListener("change", update);
  }, []);

  React.useEffect(() => {
    if (prefersReducedMotion || words.length === 0) return;

    const currentWord = words[wordIndex];
    let delay: number;

    switch (phase) {
      case "typing":
        if (charIndex < currentWord.length) {
          delay = typingSpeed;
        } else {
          setPhase("holding");
          return;
        }
        break;
      case "holding":
        delay = holdDuration;
        break;
      case "deleting":
        if (charIndex > 0) {
          delay = deletingSpeed;
        } else {
          setPhase("pausing");
          return;
        }
        break;
      case "pausing":
        delay = pauseDuration;
        break;
    }

    const timeoutId = window.setTimeout(() => {
      switch (phase) {
        case "typing":
          setCharIndex((i) => i + 1);
          break;
        case "holding":
          setPhase("deleting");
          break;
        case "deleting":
          setCharIndex((i) => i - 1);
          break;
        case "pausing":
          setWordIndex((i) => (i + 1) % words.length);
          setCharIndex(0);
          setPhase("typing");
          break;
      }
    }, delay);

    return () => window.clearTimeout(timeoutId);
  }, [wordIndex, charIndex, phase, prefersReducedMotion, words, typingSpeed, deletingSpeed, holdDuration, pauseDuration]);

  if (words.length === 0) return null;

  const currentWord = words[wordIndex];
  const displayedText = prefersReducedMotion
    ? currentWord
    : currentWord.slice(0, charIndex);

  const isAnimating = phase === "typing" || phase === "deleting";

  return (
    <span
      className={cn("inline-flex items-baseline", className)}
      data-current-word={currentWord}
    >
      <span className="sr-only">{currentWord}</span>
      <span aria-hidden="true">{displayedText}</span>
      {!prefersReducedMotion && (
        <span
          aria-hidden="true"
          className={cn(
            "inline-block w-[0.08em] self-stretch ml-[0.04em]",
            isAnimating ? "opacity-100" : "animate-blink",
            cursorClassName,
          )}
          style={{ background: "currentColor" }}
        />
      )}
    </span>
  );
}
